/* eslint-disable no-nested-ternary, react/display-name, no-param-reassign */
import React, {
  forwardRef,
  memo,
  PropsWithChildren,
  useEffect,
  useRef,
} from 'react';

import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';

import * as S from './style';

interface IProps {
  /** 1줄에 보이는 개수 (0: 아이템 너비를 컨텐츠 크기로 자동 지정) */
  perView?: number;
  /** column-gap */
  colGap?: number;
  /** row-gap */
  rowGap?: number;
  /** 좌, 우 여백값 */
  offsetLR?: number;
  /** 상단 여백값 */
  marginT?: number;
  /** 하단 여백값 */
  marginB?: number;
  /** 상단 여백값 */
  paddingT?: number;
  /** 하단 여백값 */
  paddingB?: number;
  /** 보이는 줄 개수 */
  line?: number;
  /** snap */
  snap?: boolean;
  /** scrollInit */
  scrollInit?: boolean;
  /** draggable */
  draggable?: boolean;
  /** className */
  className?: string;
  /** 선택된 아이템으로 스크롤 이동 */
  selectedIndex?: number;
  /** 스크롤 정렬 방식 */
  scrollAlign?: 'left' | 'center';
  /** onTouchStart */
  onTouchStart?: (e: React.TouchEvent<HTMLDivElement>) => void;
  /** onTouchEnd */
  onTouchEnd?: (e: React.TouchEvent<HTMLDivElement>) => void;
  /** onScroll */
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
}

const ItemsScrollBar = forwardRef(
  (
    {
      className,
      perView = 2,
      offsetLR = 20,
      colGap = 6,
      rowGap = 24,
      marginT = 0,
      marginB = 0,
      paddingT = 0,
      paddingB = 0,
      children,
      line = 1,
      snap = false,
      scrollInit = false,
      draggable = true,
      selectedIndex,
      scrollAlign = 'left',
      onTouchStart,
      onTouchEnd,
      onScroll,
    }: PropsWithChildren<IProps>,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    const mergedRef = (node: HTMLDivElement) => {
      (scrollRef as React.MutableRefObject<HTMLDivElement>).current = node;
      const forwardedRef = ref;
      if (forwardedRef) {
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else
          (forwardedRef as React.MutableRefObject<HTMLDivElement>).current =
            node;
      }
    };

    const param = {
      className: classNameBind([
        'items-scrollBar list',
        perView === 0 ? 'item-auto' : '',
        className || '',
      ]),
      $perView: perView,
      $offsetLR: offsetLR,
      $colGap: colGap,
      $rowGap: rowGap,
      $marginT: marginT,
      $marginB: marginB,
      $paddingT: paddingT,
      $paddingB: paddingB,
      $line: line,
      $snap: snap,
      $scrollAlign: scrollAlign,
      ref: mergedRef,
      draggable,
    };

    useEffect(() => {
      if (scrollInit && scrollRef.current) {
        scrollRef.current.scrollTo({ left: 0 });
      }
    }, [children, scrollInit]);

    useEffect(() => {
      if (selectedIndex === undefined) return undefined;

      // 초기 마운트 시 아이템 너비(이미지/max-content) 확정 후 위치를 읽도록
      // 다음 프레임에 스크롤 (offsetLeft 오측정 방지)
      const id = requestAnimationFrame(() => {
        const container = scrollRef.current;
        if (!container) return;
        const isItemAuto = perView === 0;
        const itemParent = isItemAuto
          ? (container.children[0] as HTMLElement)
          : container;
        const item = itemParent?.children[selectedIndex] as
          | HTMLElement
          | undefined;
        if (!item) return;
        let scrollLeft: number;
        if (scrollAlign === 'center') {
          scrollLeft =
            item.offsetLeft + item.offsetWidth / 2 - container.offsetWidth / 2;
        } else if (isItemAuto) {
          scrollLeft = item.offsetLeft - offsetLR;
        } else {
          scrollLeft = item.offsetLeft;
        }
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      });

      return () => cancelAnimationFrame(id);
    }, [selectedIndex, scrollAlign, offsetLR, perView]);

    // 배열을 n개씩 나누는 함수
    const chunkArray = (Child: React.ReactNode, size: number) => {
      const array = React.Children.toArray(Child);

      // 아이템의 개수가 size보다 작거나 같을 때 전체 배열을 하나의 청크로 반환
      if (array.length <= size) {
        return [array];
      }

      const remainder = array.length % size;
      const chunkedArray = [];
      let startIndex = 0;

      for (let i = 0; i < size; i++) {
        const endIndex =
          startIndex +
          Math.floor(array.length / size) +
          (i < remainder ? 1 : 0);
        chunkedArray.push(array.slice(startIndex, endIndex));
        startIndex = endIndex;
      }

      return chunkedArray;
    };

    // 배열을 n개씩 나누기
    const dividedArray = chunkArray(children, param.$line);

    const renderContent = () => {
      if (param.$line === 1) {
        if (param.$perView === 0) {
          return <div className="inner-items-scrollBar">{children}</div>;
        }
        return <>{children}</>;
      }

      return dividedArray.map((chunk, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className="inner-items-scrollBar" key={index}>
          {chunk.map((item, itemIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={itemIndex}>{item}</React.Fragment>
          ))}
        </div>
      ));
    };

    return (
      <S.ItemsScrollBar
        {...param}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onScroll={onScroll}
      >
        {renderContent()}
      </S.ItemsScrollBar>
    );
  }
);
ItemsScrollBar.displayName = 'ItemsScrollBar';
export default memo(ItemsScrollBar);
